import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button, Modal, ModalDialogue } from '@sg/garnish';
import { OrderState } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderTerminalStateModal = (props: Props) => {
  const { hasCurrentUserItems, hostDisplayName, orderChannel, orderState, onDismiss } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const { headlineMessage, bodyMessage } = getTerminalStateMessages({
    hostDisplayName,
    orderState,
  });
  const messageValues = {
    has_items: hasCurrentUserItems,
    host_display_name: hostDisplayName,
    order_channel: orderChannel,
  };

  const headline = formatMessage(headlineMessage, messageValues);
  const body = formatMessage(bodyMessage, messageValues);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible
      fitHeight
      size="medium"
      withCloseBtn={false}
      accessibilityLabel={headline}
      headline={headline}
      body={body}
      onRequestClose={onDismiss}
    >
      <Modal.Footer withoutTopBorder>
        <Button palette="primary" size="large" onPress={onDismiss}>
          <FormattedMessage {...messages.gotIt} />
        </Button>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTerminalStateMessages(params: Params) {
  const { hostDisplayName, orderState } = params;

  const isCompleted = orderState === OrderState.Complete;

  if (isCompleted && hostDisplayName) {
    return {
      headlineMessage: messages.completedHeadline,
      bodyMessage: messages.completedBody,
    };
  }

  if (isCompleted) {
    return {
      headlineMessage: messages.completedHeadlineWithoutHost,
      bodyMessage: messages.completedBodyWithoutHost,
    };
  }

  if (hostDisplayName) {
    return {
      headlineMessage: messages.canceledHeadline,
      bodyMessage: messages.canceledBody,
    };
  }

  return {
    headlineMessage: messages.canceledHeadlineWithoutHost,
    bodyMessage: messages.canceledBody,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  completedHeadline: {
    defaultMessage: '{host_display_name} submitted the group order',
    description: 'Group Order | Completed modal headline',
  },
  completedBody: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to {host_display_name} for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body',
  },
  canceledHeadline: {
    defaultMessage: "{host_display_name}'s group order has ended",
    description: 'Group Order | Canceled modal headline',
  },
  canceledBody: {
    defaultMessage: 'Your items were removed. Nothing was charged to you.',
    description: 'Group Order | Canceled modal body',
  },
  completedHeadlineWithoutHost: {
    defaultMessage: 'The group order was submitted',
    description: 'Group Order | Completed modal headline without a host name',
  },
  completedBodyWithoutHost: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to the host for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body without a host name',
  },
  canceledHeadlineWithoutHost: {
    defaultMessage: 'This group order has ended',
    description: 'Group Order | Canceled modal headline without a host name',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Group Order | Terminal state modal CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  hasCurrentUserItems: boolean;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
  onDismiss: () => void;
};

type Params = {
  hostDisplayName: string | undefined;
  orderState: OrderState.Complete | OrderState.Discarded;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
